import React, { useState, useEffect, useContext } from 'react';
import Loading from 'components/Loading';
import MultiStepModal, { ModalStepT } from 'ui/Modals/MultiStepModal';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'store/hooks';
import { useAddInscription } from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/hooks/inscriptions';
import { BudgetBys, BudgetExpenseOrIncome } from 'business/constants';
import { BUDGET_LINE_ID } from 'routes/BudgetModule/config/constants';
import {
    BudgetLine,
    ResourceNameField,
    BudgetProposalT,
    ResourceBudgetProposalAssociationT,
} from 'routes/BudgetModule/config/types';
import {
    getImputationDimensionsCodes,
    EXCLUDED_DIMENSIONS_BY_CONTEXT,
} from 'routes/BudgetModule/config/hooks';
import {
    getResourceType,
    isByGestionnaire,
} from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { styled } from '@mui/material/styles';
import { isCustomBudgetProposal } from 'routes/BudgetModule/components/BudgetProposal/Resource/hooks/budgetProposal';
import {
    getResourceColumns,
    getBudgetLineColumns,
} from './helpers/colDefinitions';
import RadioCellSelector from './RadioCellSelector';
import { AddInscriptionModalTitles } from './types';
import { buildOrphanInscriptionPayload } from './helpers/dataHelpers';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { useGetAddBudgetLines } from 'routes/BudgetModule/helpers/hooks/useGetBudgetLines';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';

const StySubtitle = styled(Typography, {
    name: 'StySubtitle',
})({
    margin: '10px 0 30px 0',
});

type Props = {
    stepTitles: AddInscriptionModalTitles;
    isOpen: boolean;
    onClose: (
        isOrphan?: boolean,
        budgetProposal?: BudgetProposalT,
        resourceName?: string,
    ) => void;
    budgetProposalResources: ResourceBudgetProposalAssociationT[];
    resourceType: ResourceNameField;
    expenseOrIncome: BudgetExpenseOrIncome;
    showOperationColumn?: boolean;
    allowOrphanInscription: boolean;
    operationSoftwareId?: string;
    isOperationDraft?: boolean;
    inscriptionQueryCacheKey: InscriptionQueryKey;
};

function NewInscriptionModal({
    stepTitles,
    isOpen,
    onClose: onCloseProp,
    budgetProposalResources,
    resourceType,
    expenseOrIncome,
    showOperationColumn = false,
    allowOrphanInscription = false,
    operationSoftwareId = undefined,
    isOperationDraft = false,
    inscriptionQueryCacheKey,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [isOrphan, setIsOrphan] = useState(false);

    const initialChoice = {
        budgetProposalResource:
            budgetProposalResources.length > 0
                ? budgetProposalResources[0]
                : null,
        budgetLine: null,
    };

    const [selectedBudgetProposalResource, setSelectedBudgetProposalResource] =
        useState<null | ResourceBudgetProposalAssociationT>(
            initialChoice.budgetProposalResource,
        );
    // selectedBudgetLine is null whenever the modal is closed.
    const [selectedBudgetLine, setSelectedBudgetLine] = useState<
        BudgetLine | null | undefined
    >(initialChoice.budgetLine);

    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsOrphan(event.target.checked);
        setSelectedBudgetLine(null);
    };

    useEffect(() => {
        if (selectedBudgetLine !== null) {
            setIsOrphan(false);
        }
    }, [selectedBudgetLine]);

    const imputationRawDimensions = React.useContext(
        ImputationDimensionsContext,
    );

    // TODO: Modify as part of epic "Améliorer la gestion des col defs de Budget par le back" (Shortcut ID: 21594)
    const imputationDimensions = getImputationDimensionsCodes(
        imputationRawDimensions,
        [
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.BudgetProposal,
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.ExpenseIncome,
            ...(showOperationColumn
                ? []
                : EXCLUDED_DIMENSIONS_BY_CONTEXT.Operation),
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.SuperOperation,
            ...(isByGestionnaire(resourceType)
                ? EXCLUDED_DIMENSIONS_BY_CONTEXT.Gestionnaire
                : EXCLUDED_DIMENSIONS_BY_CONTEXT.OrgChartFinance),
        ],
    );

    const { data: budgetLines = [], isLoading: areBudgetLinesLoading } =
        useGetAddBudgetLines({
            budgetBy:
                selectedBudgetProposalResource?.budgetProposal.by ??
                BudgetBys.GESTIONNAIRE,
            budgetName:
                selectedBudgetProposalResource?.budgetProposal.budgetName,
            investmentOperating:
                selectedBudgetProposalResource?.budgetProposal.section,
            exercice: selectedBudgetProposalResource?.budgetProposal.exercice,
            expenseOrIncome,
            resourceSoftwareId:
                selectedBudgetProposalResource?.resource?.resourceSoftwareId,
            operationSoftwareId: operationSoftwareId,
            enabled: selectedBudgetProposalResource != null && isOpen,
        });

    const onClose = () => {
        const resourceName =
            initialChoice.budgetProposalResource?.resource?.resourceName;
        onCloseProp(
            isOrphan,
            initialChoice.budgetProposalResource?.budgetProposal,
            resourceName,
        );
    };
    const orphanInscriptionSwitch = (
        <>
            <Switch
                checked={isOrphan}
                onChange={handleSwitchChange}
                name="OrphanInscriptionSwitch"
            />
            <Typography variant="body2" display="inline">
                {t('budgetProposal.doNotClassify')}
            </Typography>
        </>
    );

    const mutationBody =
        (isOrphan || isOperationDraft) && selectedBudgetProposalResource
            ? buildOrphanInscriptionPayload(
                  expenseOrIncome,
                  selectedBudgetProposalResource,
                  operationSoftwareId,
                  isOperationDraft,
              )
            : {
                  budgetLineId: selectedBudgetLine
                      ? selectedBudgetLine[BUDGET_LINE_ID]
                      : -1,
              };

    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);
    const resource = isSchool
        ? t('budgetProposal.schoolResource')
        : t(
              `budget.${getResourceType(
                  selectedBudgetProposalResource?.budgetProposal.by ??
                      BudgetBys.GESTIONNAIRE,
              )}`,
          );

    const addInscriptionMutation = useAddInscription(
        (error) => {
            onClose();
            dispatch.vision.notifyUser({
                message: t(error.message, {
                    resource,
                }),
                snackType: 'error',
            });
        },
        (message) => {
            onClose();
            dispatch.vision.notifyUser({
                message,
                snackType: 'success',
            });
            trackEventBudget(TRACKABLE_EVENTS.budget.general.addInscription);
        },
        queryClient,
        true,
        selectedBudgetProposalResource?.budgetProposal.phase ?? null,
        mutationBody,
        inscriptionQueryCacheKey,
    );

    const steps: ModalStepT[] = [
        {
            // First step is for selecting a budget proposal by resource.
            confirmAction: {
                onClick: () => {
                    if (isOperationDraft) {
                        addInscriptionMutation.mutate();
                    } else {
                        setSelectedBudgetLine(
                            budgetLines && budgetLines.length > 0
                                ? budgetLines[0]
                                : null,
                        );
                    }
                },
                label: isOperationDraft
                    ? 'budgetProposal.addInscription'
                    : 'common.next',
                enabled: selectedBudgetProposalResource !== null,
            },
            stepBackAction: {
                onClick: () =>
                    setSelectedBudgetProposalResource(
                        initialChoice.budgetProposalResource,
                    ),
            },
            node: (
                <>
                    <Typography variant="body1SemiBold">
                        {stepTitles.budgetProposalSelection.title}
                    </Typography>
                    <StySubtitle variant="body2">
                        {stepTitles.budgetProposalSelection.subTitle}
                    </StySubtitle>
                    <RadioCellSelector
                        dataRows={budgetProposalResources}
                        selectedRow={selectedBudgetProposalResource}
                        setSelectedRow={setSelectedBudgetProposalResource}
                        columns={getResourceColumns(resourceType)}
                    />
                </>
            ),
        },
    ];

    if (!isOperationDraft) {
        steps.push({
            // Second step is for selecting a budget line corresponding to
            // the selected budget proposal by resource.
            confirmAction: {
                enabled: selectedBudgetLine !== null || isOrphan,
                label: 'budgetProposal.addInscription',
                onClick: () => addInscriptionMutation.mutate(),
                shouldTriggerDefaultOnClose: false,
            },
            stepBackAction: {
                onClick: () => {
                    setSelectedBudgetLine(initialChoice.budgetLine);
                    setIsOrphan(false);
                },
            },
            leftActionGroup: allowOrphanInscription ? (
                orphanInscriptionSwitch
            ) : (
                <></>
            ),
            node: areBudgetLinesLoading ? (
                <Loading />
            ) : (
                <>
                    <Typography variant="body1SemiBold">
                        {stepTitles.budgetLineSelection.title}
                    </Typography>
                    <StySubtitle variant="body2">
                        {stepTitles.budgetLineSelection.subTitle}
                    </StySubtitle>
                    <RadioCellSelector
                        dataRows={budgetLines}
                        selectedRow={selectedBudgetLine}
                        setSelectedRow={setSelectedBudgetLine}
                        columns={getBudgetLineColumns(imputationDimensions)}
                    />
                </>
            ),
        });
    }

    return (
        <MultiStepModal
            isOpen={isOpen}
            onClose={onClose}
            maxWidth="lg"
            steps={steps}
            shouldRunStepBackOnReset={false}
        />
    );
}

export default NewInscriptionModal;
